import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { useField } from "formik"
import { isEqual } from "lodash-es"

import type { NullableCalendarEvent } from "./calendar/fullCalendarTypes"
import type { OrganizerSelectedValue, ResourceItem } from "../../createOrderTypes"
import type { MeetingServiceOrder } from "../../../../../modules/merchant/accounting/accounting/accountingTypes"
import { CreateOrderFlowType } from "../../createOrderTypes"
import type { BookingSeed } from "./helpers"
import { buildBookingSeedFromOrder } from "./helpers"
import { defaultInitialEventState } from "../../defaults"
import { getBookingResourceWithTableSetup } from "../form/helpers"
import { getResponsibleSeed } from "../../../../../modules/order/pages/helpers"
import { useCreateOrderForm } from "../../mainForm/CreateOrderFormContext"
import { useDateTime } from "../../../../../modules/localization/useDateTime"
import { useOrderDialogUrlState } from "../../hooks/useOrderDialogUrlState"
import {
    CreateUpdateBookingAttendee,
    CreateUpdateBookingResourceTableSetups,
} from "../../../dialogs/createUpdateBookingDialog/createUpdateBookingTypes"

export type InitialValuesState = {
    id: number | null
    meetingName: string
    organizers: OrganizerSelectedValue
    attendees: CreateUpdateBookingAttendee[]
    resources: CreateUpdateBookingResourceTableSetups[]
    messageToServiceCenter?: string
    isPrivate?: boolean
    isExternal?: boolean
    workgroupId: string | null
}

export type InitialEventState = {
    event: NullableCalendarEvent
    initValues: InitialValuesState | null
    isUpdated: boolean
}

type HandleChangeInitEventStateParams = {
    event?: InitialEventState["event"]
    initValues?: InitialEventState["initValues"]
    isUpdated?: InitialEventState["isUpdated"]
    fullResources?: ResourceItem[]
}

type ResourceBookingDraftDataValue = Readonly<{
    draftEvent: NullableCalendarEvent
    fullResources: ResourceItem[]
    initialEventStateId?: number | null
    isUpdatedOrder: boolean
}>

type ResourceBookingDraftActionsValue = Readonly<{
    setDraftEvent: (event: NullableCalendarEvent, fullResources?: ResourceItem[]) => void
    setAndSyncDraftEvent: (event: NullableCalendarEvent, resources: ResourceItem[]) => void
    syncDraftToFormik: () => void
    handleChangeInitEventState: (params: HandleChangeInitEventStateParams) => void
    handleCancelClick: () => void
}>

export type ResourceBookingDraftContextValue = ResourceBookingDraftDataValue & ResourceBookingDraftActionsValue

const ResourceBookingDraftDataContext = createContext<ResourceBookingDraftDataValue | null>(null)
const ResourceBookingDraftActionsContext = createContext<ResourceBookingDraftActionsValue | null>(null)

type ResourceBookingDraftProviderProps = Readonly<{
    children: React.ReactNode
    orderDetails?: MeetingServiceOrder
}>

export function ResourceBookingDraftProvider({ children, orderDetails }: ResourceBookingDraftProviderProps) {
    const dateTimeFactory = useDateTime()
    const { orderContext, flowConfig } = useCreateOrderForm()
    const { mode } = orderContext
    const {
        config: { bookingPrefill },
    } = useOrderDialogUrlState()

    // Write-only: the draft never reads its value back out of Formik (see `buildBookingSeedFromOrder`).
    const [, , eventHelpers] = useField<NullableCalendarEvent>("event")
    const [, , fullResourcesHelpers] = useField<ResourceItem[]>("fullResourcesList")

    const eventHelpersRef = useRef(eventHelpers)
    eventHelpersRef.current = eventHelpers
    const fullResourcesHelpersRef = useRef(fullResourcesHelpers)
    fullResourcesHelpersRef.current = fullResourcesHelpers

    const initialSeedRef = useRef<BookingSeed | null>(null)
    if (!initialSeedRef.current) {
        initialSeedRef.current = buildBookingSeedFromOrder({
            orderDetails,
            dateTimeFactory,
            mode,
            flowConfig,
            bookingPrefill,
        })
    }

    const [draftEvent, setDraftEventState] = useState<NullableCalendarEvent>(initialSeedRef.current.event)
    const [fullResources, setFullResourcesState] = useState<ResourceItem[]>(initialSeedRef.current.fullResourcesList)
    const [isUpdatedOrder, setIsUpdatedOrder] = useState<boolean>(defaultInitialEventState.isUpdated)

    const draftEventRef = useRef(draftEvent)
    draftEventRef.current = draftEvent
    const fullResourcesRef = useRef(fullResources)
    fullResourcesRef.current = fullResources

    const refInitialEvent = useRef<NullableCalendarEvent>(defaultInitialEventState.event)
    const refInitialFullResources = useRef<ResourceItem[]>([])
    const refInitValues = useRef<InitialValuesState | null>(defaultInitialEventState.initValues)
    const isOutlookFlow = flowConfig?.flow === CreateOrderFlowType.OUTLOOK
    const initialEventStateId = refInitValues.current?.id

    const setDraftEvent = useCallback((event: NullableCalendarEvent, nextFullResources?: ResourceItem[]) => {
        setDraftEventState(event)
        if (nextFullResources !== undefined) {
            setFullResourcesState(nextFullResources)
        } else if (!event) {
            setFullResourcesState([])
        }
    }, [])

    useEffect(() => {
        const initValues = refInitValues.current
        if (initValues && draftEvent && !isEqual(draftEvent, refInitialEvent.current)) {
            setIsUpdatedOrder(true)
        }
    }, [draftEvent])

    // Every input `buildBookingSeedFromOrder` reads, flattened to primitives. Keying the reseed on
    // this rather than on Formik values means no write to `values.event` can move a booking the user
    // is editing, and a refetch returning the same booking leaves the draft alone.
    const seedKey = orderDetails
        ? [
              orderDetails.id ?? "",
              orderDetails.meetingDetails?.startDateTime ?? "",
              orderDetails.meetingDetails?.endDateTime ?? "",
              (orderDetails.bookingDetails?.resources ?? []).map((resource) => resource.id).join(","),
              mode ?? "",
              Boolean(flowConfig),
          ].join("|")
        : ""

    useEffect(() => {
        const { event, fullResourcesList } = buildBookingSeedFromOrder({
            orderDetails,
            dateTimeFactory,
            mode,
            flowConfig,
            bookingPrefill,
        })

        setDraftEventState(event)
        setFullResourcesState(fullResourcesList)
        refInitialEvent.current = event
        refInitialFullResources.current = fullResourcesList

        if (orderDetails) {
            // `meetingDetails` is optional-chained for the reason given in `buildBookingSeedFromOrder`.
            const { id, meetingDetails, bookingDetails, messageToServiceCenter } = orderDetails
            const person = getResponsibleSeed(
                orderDetails.organizers,
                orderDetails.customer?.id,
                orderDetails.customer?.email,
            )
            refInitValues.current = {
                id,
                meetingName: meetingDetails?.meetingName || "",
                organizers: person!,
                attendees: meetingDetails?.attendees || [],
                resources: bookingDetails?.resources ? getBookingResourceWithTableSetup(bookingDetails.resources) : [],
                messageToServiceCenter: messageToServiceCenter ?? "",
                isPrivate: meetingDetails?.isPrivate || false,
                isExternal: meetingDetails?.isExternal || false,
                workgroupId: meetingDetails?.workgroup ? String(meetingDetails.workgroup.id) : null,
            }
        } else {
            refInitValues.current = defaultInitialEventState.initValues
        }

        setIsUpdatedOrder(false)
    }, [seedKey])

    const handleCancelClick = useCallback(() => {
        const initValues = refInitValues.current
        const shouldResetToInitialEvent = initValues?.id || (!initValues?.id && isOutlookFlow)

        if (shouldResetToInitialEvent) {
            const initialEvent = refInitialEvent.current
            const initialFullResources = refInitialFullResources.current
            setDraftEvent(initialEvent ?? null, initialEvent ? initialFullResources : undefined)
            setIsUpdatedOrder(false)
        } else {
            setDraftEvent(null)
        }
    }, [isOutlookFlow, setDraftEvent])

    const syncDraftToFormik = useCallback(() => {
        eventHelpersRef.current.setValue(draftEventRef.current)
        fullResourcesHelpersRef.current.setValue(fullResourcesRef.current)
    }, [])

    const setAndSyncDraftEvent = useCallback((event: NullableCalendarEvent, resources: ResourceItem[]) => {
        // a new draft object redraws the whole timeline, so an unchanged one keeps the old reference
        setDraftEventState((prev) => (isEqual(prev, event) ? prev : event))
        setFullResourcesState((prev) => (isEqual(prev, resources) ? prev : resources))
        eventHelpersRef.current.setValue(event)
        fullResourcesHelpersRef.current.setValue(resources)
    }, [])

    const handleChangeInitEventState = useCallback(
        ({ event, initValues, isUpdated, fullResources }: HandleChangeInitEventStateParams) => {
            if (event !== undefined) refInitialEvent.current = event
            if (initValues !== undefined) refInitValues.current = initValues
            if (fullResources !== undefined) refInitialFullResources.current = fullResources
            if (isUpdated !== undefined) setIsUpdatedOrder(isUpdated)
        },
        [],
    )

    const dataValue = useMemo<ResourceBookingDraftDataValue>(
        () => ({
            draftEvent,
            fullResources,
            initialEventStateId,
            isUpdatedOrder,
        }),
        [draftEvent, fullResources, initialEventStateId, isUpdatedOrder],
    )

    const actionsValue = useMemo<ResourceBookingDraftActionsValue>(
        () => ({
            setDraftEvent,
            setAndSyncDraftEvent,
            syncDraftToFormik,
            handleChangeInitEventState,
            handleCancelClick,
        }),
        [setDraftEvent, setAndSyncDraftEvent, syncDraftToFormik, handleChangeInitEventState, handleCancelClick],
    )

    return (
        <ResourceBookingDraftDataContext.Provider value={dataValue}>
            <ResourceBookingDraftActionsContext.Provider value={actionsValue}>
                {children}
            </ResourceBookingDraftActionsContext.Provider>
        </ResourceBookingDraftDataContext.Provider>
    )
}

export function useResourceBookingDraft(): ResourceBookingDraftContextValue {
    const data = useContext(ResourceBookingDraftDataContext)
    const actions = useContext(ResourceBookingDraftActionsContext)
    if (!data || !actions) {
        throw new Error("useResourceBookingDraft must be used within ResourceBookingDraftProvider")
    }
    return { ...data, ...actions }
}

export function useResourceBookingDraftData(): ResourceBookingDraftDataValue {
    const context = useContext(ResourceBookingDraftDataContext)
    if (!context) {
        throw new Error("useResourceBookingDraftData must be used within ResourceBookingDraftProvider")
    }
    return context
}

export function useResourceBookingDraftActions(): ResourceBookingDraftActionsValue {
    const context = useContext(ResourceBookingDraftActionsContext)
    if (!context) {
        throw new Error("useResourceBookingDraftActions must be used within ResourceBookingDraftProvider")
    }
    return context
}
